import { Alert, Box, Button, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import React from "react";
import { richText } from "./richText";
import { isCoarsePointerDevice } from "./deviceInput";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DifficultyLevelConfig {
  min: number;
  max: number;
  point_value: number;
}

export interface MathEffortMotivationalMessage {
  at_problem: number;
  message: string;
}

export interface MathEffortFinishPage {
  title?: string;
  instructions?: string[];
}

export interface MathEffortTrainingConfig {
  problems_per_level: number;
  numbers_per_problem: number;
  display_time_ms: number;
  response_time_limit_ms: number;
  mobile_response_time_limit_ms: number;
  inter_stimulus_interval_ms: number;
  motivational_messages: MathEffortMotivationalMessage[];
  /** Páginas exibidas ao fim do treino, após a página de resultados. */
  finish?: MathEffortFinishPage[];
}

export interface MathEffortTaskConfig {
  total_problems: number;
  block_size: number;
  numbers_per_problem: number;
  display_time_ms: number;
  response_time_limit_ms: number;
  mobile_response_time_limit_ms: number;
  inter_stimulus_interval_ms: number;
  difficulty_change_time_ms: number;
  motivational_messages: MathEffortMotivationalMessage[];
}

export interface MathEffortIntroductionPage {
  title?: string;
  instructions?: string[];
  /** Quando true, exibe o quadro com os níveis de dificuldade nesta página. */
  show_difficulty_levels?: boolean;
}

export interface MathEffortConfig {
  difficulty_levels: Record<string, DifficultyLevelConfig>;
  training?: Partial<MathEffortTrainingConfig>;
  task?: Partial<MathEffortTaskConfig>;
  introduction?: MathEffortIntroductionPage[];
}

export interface MathEffortProblemResult {
  problem_number: number;
  numbers: number[];
  correct_answer: number;
  user_answer: number | null;
  is_correct: boolean;
  response_time_ms: number;
}

export interface MathEffortBlockResult {
  block_number: number;
  level_chosen: number;
  correct_count: number;
  error_count: number;
  omission_count: number;
  point_value: number;
  score: { earned: number; max_possible: number };
  problems: MathEffortProblemResult[];
}

export interface MathEffortLevelChangeEvent {
  at_problem: number;
  from_level: number | null;
  to_level: number;
}

export interface MathEffortTaskAnswer {
  total_correct: number;
  total_errors: number;
  total_omissions: number;
  score: { earned: number; max_possible: number };
  blocks: MathEffortBlockResult[];
  level_change_events: MathEffortLevelChangeEvent[];
}

interface Props {
  onAnswerChange: (taskAnswer?: MathEffortTaskAnswer) => void;
  mathEffortConfig?: Partial<MathEffortConfig>;
}

type Phase =
  | "intro"
  | "select_level"
  | "showing_numbers"
  | "answering"
  | "feedback"
  | "motivation"
  | "level_changing"
  | "training_done"
  | "done";

type TrainingFeedbackOutcome = "correct" | "error" | "omission";

// ─── Internal resolved types ───────────────────────────────────────────────────

interface ResolvedDifficultyLevel {
  min: number;
  max: number;
  point_value: number;
}

interface ResolvedTrainingConfig {
  problems_per_level: number;
  numbers_per_problem: number;
  display_time_ms: number;
  response_time_limit_ms: number;
  mobile_response_time_limit_ms: number;
  inter_stimulus_interval_ms: number;
  motivational_messages: Array<{ at_problem: number; message: string }>;
}

interface ResolvedTaskConfig {
  total_problems: number;
  block_size: number;
  numbers_per_problem: number;
  display_time_ms: number;
  response_time_limit_ms: number;
  mobile_response_time_limit_ms: number;
  inter_stimulus_interval_ms: number;
  difficulty_change_time_ms: number;
  motivational_messages: Array<{ at_problem: number; message: string }>;
}

interface ResolvedConfig {
  difficulty_levels: Record<string, ResolvedDifficultyLevel>;
  training: ResolvedTrainingConfig | null;
  task: ResolvedTaskConfig;
}

// ─── Constants / defaults ─────────────────────────────────────────────────────

// Feedback de certo/errado exibido apenas durante o treino.
const TRAINING_FEEDBACK_CORRECT_MS = 1200;
const TRAINING_FEEDBACK_ERROR_MS = 2200;

// Contagem regressiva exibida antes de iniciar o treino, a tarefa e o bloco de
// um nível confirmado.
const START_COUNTDOWN_SECONDS = 3;

const DEFAULT_TASK_CONFIG: ResolvedTaskConfig = {
  total_problems: 100,
  block_size: 5,
  numbers_per_problem: 4,
  display_time_ms: 800,
  response_time_limit_ms: 1500,
  mobile_response_time_limit_ms: 5000,
  inter_stimulus_interval_ms: 500,
  difficulty_change_time_ms: 2000,
  motivational_messages: [
    { at_problem: 25, message: "Você está indo bem! Continue assim!" },
    { at_problem: 50, message: "Metade do caminho! Mantenha o foco!" },
    { at_problem: 75, message: "Quase lá! Dê o seu melhor!" },
  ],
};

const DEFAULT_TRAINING_CONFIG: ResolvedTrainingConfig = {
  problems_per_level: 2,
  numbers_per_problem: 4,
  display_time_ms: 800,
  response_time_limit_ms: 1500,
  mobile_response_time_limit_ms: 5000,
  inter_stimulus_interval_ms: 500,
  motivational_messages: [],
};

const DEFAULT_DIFFICULTY_LEVELS: Record<string, ResolvedDifficultyLevel> = {
  "1": { min: 1, max: 3, point_value: 1 },
  "2": { min: 3, max: 9, point_value: 2 },
  "3": { min: 7, max: 15, point_value: 3 },
  "4": { min: 7, max: 25, point_value: 4 },
  "5": { min: 7, max: 35, point_value: 5 },
};

interface ResolvedIntroductionPage {
  title: string;
  instructions: string[];
  show_difficulty_levels: boolean;
}

const DEFAULT_MATH_INTRO_TITLE = "Tarefa de Cálculo Mental";
// Exportado junto com DEFAULT_MATH_TRAINING_FINISH_PAGES para permitir verificar
// a cópia da especificação nos testes.
export const DEFAULT_MATH_INTRODUCTION_PAGES: ResolvedIntroductionPage[] = [
  {
    title: DEFAULT_MATH_INTRO_TITLE,
    instructions: [
      "<strong>Instrução geral:</strong> Nesta tarefa, você realizará <strong>100 exercícios de adição mental.</strong> Para cada problema, você verá <strong>quatro números</strong> aparecerem rapidamente, <strong>um de cada vez</strong>, no centro da tela. Sua tarefa é prestar bastante atenção e <strong>somar mentalmente esses quatro números.</strong> Quando todos os números tiverem sido apresentados, <strong>digite o resultado da soma.</strong>",
    ],
    show_difficulty_levels: false,
  },
  {
    title: DEFAULT_MATH_INTRO_TITLE,
    instructions: [
      "A tarefa possui <strong>cinco níveis de dificuldade</strong>, que variam de acordo com o tamanho dos números apresentados. <strong>Abaixo, você pode visualizar os números de cada nível.</strong>",
      "Você terá <strong>15 segundos</strong> para resolver cada problema.",
      "Vamos começar com <strong>um treino de cinco problemas, um de cada nível,</strong> para que você entenda o funcionamento da tarefa e os seus níveis de dificuldade. Os problemas começarão pelo <strong>nível 1</strong> e irão até o <strong>nível 5.</strong>",
    ],
    show_difficulty_levels: true,
  },
];

interface ResolvedFinishPage {
  title: string;
  instructions: string[];
}

// Exportado para que os testes possam verificar a cópia da especificação sem
// precisar percorrer todo o treino até a tela de fim.
export const DEFAULT_MATH_TRAINING_FINISH_PAGES: ResolvedFinishPage[] = [
  {
    title: "Como funciona a tarefa",
    instructions: [
      "Você poderá <strong>selecionar no topo da tela</strong> em qual nível deseja trabalhar. Sempre que escolher um nível, <strong>cinco problemas de adição</strong> serão apresentados nesse nível. Ao finalizar a resolução desses cinco problemas, você terá a opção de <strong>manter o nível atual ou selecionar um novo nível</strong> para os próximos cinco problemas.",
    ],
  },
  {
    title: "Mudança de nível e pontuação",
    instructions: [
      "O nível de dificuldade dos problemas <strong>pode ser alterado a qualquer momento</strong>, mesmo antes de completar os cinco problemas do <strong>nível atual.</strong> Para mudar de nível, <strong>basta selecionar o nível desejado no topo da tela.</strong>",
      "Por exemplo, se você selecionar o nível 2, você pode completar o bloco de cinco problemas nesse nível ou pode mudar de nível a qualquer momento. Se decidir mudar para o nível 4 após resolver apenas três problemas do nível 2, a sequência atual é interrompida e os <strong>próximos cinco problemas</strong> serão apresentados no nível 4.",
      "Nesta tarefa, sua <strong>pontuação depende do nível de dificuldade escolhido e da quantidade de respostas corretas.</strong> Quanto mais alto o nível de dificuldade, maior será a pontuação obtida.",
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const safeInt = (v: unknown, fallback: number, min: number, max?: number): number => {
  const n = Number.parseInt(String(v), 10);
  if (!Number.isFinite(n)) return fallback;
  if (n < min) return min;
  if (max !== undefined && n > max) return max;
  return n;
};

const resolveMessages = (
  src?: Array<{ at_problem?: unknown; message?: unknown }>,
  defaults?: Array<{ at_problem: number; message: string }>,
): Array<{ at_problem: number; message: string }> =>
  (src ?? defaults ?? [])
    .filter((m) => m?.at_problem != null && (m.at_problem as number) > 0 && typeof m?.message === "string" && m.message)
    .map((m) => ({ at_problem: safeInt(m.at_problem, 0, 1), message: m.message as string }))
    .sort((a, b) => a.at_problem - b.at_problem);

const resolveConfig = (partial?: Partial<MathEffortConfig>): ResolvedConfig => {
  const rawLevels = partial?.difficulty_levels ?? DEFAULT_DIFFICULTY_LEVELS;
  const difficulty_levels = Object.fromEntries(
    Object.entries(rawLevels).map(([k, v]) => {
      const min = safeInt(v?.min, 1, 1, 9999);
      const max = safeInt(v?.max, 3, 1, 9999);
      const point_value = safeInt(v?.point_value, 0, 0, 9999);
      return [k, min <= max ? { min, max, point_value } : { min: max, max: min, point_value }];
    }),
  );

  const pt = partial?.task;
  const task: ResolvedTaskConfig = {
    total_problems: safeInt(pt?.total_problems, DEFAULT_TASK_CONFIG.total_problems, 1),
    block_size: safeInt(pt?.block_size, DEFAULT_TASK_CONFIG.block_size, 1),
    numbers_per_problem: safeInt(pt?.numbers_per_problem, DEFAULT_TASK_CONFIG.numbers_per_problem, 2),
    display_time_ms: safeInt(pt?.display_time_ms, DEFAULT_TASK_CONFIG.display_time_ms, 100),
    response_time_limit_ms: safeInt(pt?.response_time_limit_ms, DEFAULT_TASK_CONFIG.response_time_limit_ms, 1000),
    mobile_response_time_limit_ms: safeInt(pt?.mobile_response_time_limit_ms, DEFAULT_TASK_CONFIG.mobile_response_time_limit_ms, 1000),
    inter_stimulus_interval_ms: safeInt(pt?.inter_stimulus_interval_ms, DEFAULT_TASK_CONFIG.inter_stimulus_interval_ms, 0),
    difficulty_change_time_ms: safeInt(pt?.difficulty_change_time_ms, DEFAULT_TASK_CONFIG.difficulty_change_time_ms, 0),
    motivational_messages: resolveMessages(pt?.motivational_messages, DEFAULT_TASK_CONFIG.motivational_messages),
  };

  let training: ResolvedTrainingConfig | null = null;
  if (partial?.training !== undefined) {
    const ptrn = partial.training;
    training = {
      problems_per_level: safeInt(ptrn?.problems_per_level, DEFAULT_TRAINING_CONFIG.problems_per_level, 1),
      numbers_per_problem: safeInt(ptrn?.numbers_per_problem, DEFAULT_TRAINING_CONFIG.numbers_per_problem, 2),
      display_time_ms: safeInt(ptrn?.display_time_ms, DEFAULT_TRAINING_CONFIG.display_time_ms, 100),
      response_time_limit_ms: safeInt(ptrn?.response_time_limit_ms, DEFAULT_TRAINING_CONFIG.response_time_limit_ms, 1000),
      mobile_response_time_limit_ms: safeInt(ptrn?.mobile_response_time_limit_ms, DEFAULT_TRAINING_CONFIG.mobile_response_time_limit_ms, 1000),
      inter_stimulus_interval_ms: safeInt(ptrn?.inter_stimulus_interval_ms, DEFAULT_TRAINING_CONFIG.inter_stimulus_interval_ms, 0),
      motivational_messages: resolveMessages(ptrn?.motivational_messages, DEFAULT_TRAINING_CONFIG.motivational_messages),
    };
  }

  return { difficulty_levels, training, task };
};

const sortedLevelEntries = (levels: Record<string, ResolvedDifficultyLevel>) =>
  Object.entries(levels).sort(([a], [b]) => {
    const na = Number.parseFloat(a),
      nb = Number.parseFloat(b);
    return Number.isFinite(na) && Number.isFinite(nb)
      ? na - nb
      : a.localeCompare(b, "pt-BR", { numeric: true, sensitivity: "base" });
  });

const levelNumber = (key: string, fallback: number) => {
  const n = Number.parseInt(key, 10);
  return Number.isFinite(n) ? n : fallback;
};

const difficultyColor = (index: number, total: number): string => {
  const t = total <= 1 ? 0 : index / (total - 1);
  let r: number, g: number, b: number;
  if (t <= 0.5) {
    const s = t * 2;
    r = Math.round(0 + (180 - 0) * s);
    g = Math.round(200 + (214 - 200) * s);
    b = Math.round(83 + (0 - 83) * s);
  } else {
    const s = (t - 0.5) * 2;
    r = Math.round(255 + (213 - 255) * s);
    g = Math.round(214 + (0 - 214) * s);
    b = 0;
  }
  const hex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${hex(r)}${hex(g)}${hex(b)}`;
};

const randInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

// ─── Component ────────────────────────────────────────────────────────────────

const MathEffortQuestion: React.FC<Props> = ({
  onAnswerChange,
  mathEffortConfig,
}) => {
  const cfg = React.useMemo(() => resolveConfig(mathEffortConfig), [mathEffortConfig]);
  const introductionPages = React.useMemo<ResolvedIntroductionPage[]>(() => {
    const raw = mathEffortConfig?.introduction;

    if (Array.isArray(raw) && raw.length > 0) {
      return raw.map((page) => ({
        title: page?.title ?? DEFAULT_MATH_INTRO_TITLE,
        instructions: Array.isArray(page?.instructions) ? page.instructions : [],
        show_difficulty_levels: page?.show_difficulty_levels ?? false,
      }));
    }

    // Legado: introduction como objeto único ({ title, text, instructions }).
    if (raw && typeof raw === "object") {
      const legacy = raw as { title?: string; text?: string; instructions?: string[] };
      const instructions = Array.isArray(legacy.instructions) ? legacy.instructions : [];
      return [
        {
          title: legacy.title ?? DEFAULT_MATH_INTRO_TITLE,
          instructions: legacy.text ? [legacy.text, ...instructions] : instructions,
          show_difficulty_levels: true,
        },
      ];
    }

    return DEFAULT_MATH_INTRODUCTION_PAGES;
  }, [mathEffortConfig]);
  const trainingFinishPages = React.useMemo<ResolvedFinishPage[]>(() => {
    const raw = mathEffortConfig?.training?.finish;
    if (!Array.isArray(raw) || raw.length === 0) {
      return DEFAULT_MATH_TRAINING_FINISH_PAGES;
    }
    return raw.map((page, i) => ({
      title: page?.title ?? DEFAULT_MATH_TRAINING_FINISH_PAGES[i]?.title ?? "",
      instructions: Array.isArray(page?.instructions) ? page.instructions : [],
    }));
  }, [mathEffortConfig]);
  const levelEntries = React.useMemo(() => sortedLevelEntries(cfg.difficulty_levels), [cfg.difficulty_levels]);
  const firstLevelKey = levelEntries[0]?.[0] ?? null;
  const hasDifficultyLevels = levelEntries.length > 0;
  const hasTraining = cfg.training !== null;

  // ── Stable ref for onAnswerChange ────────────────────────────────────────
  const onAnswerChangeRef = React.useRef(onAnswerChange);
  React.useLayoutEffect(() => { onAnswerChangeRef.current = onAnswerChange; });

  // ── Timers ────────────────────────────────────────────────────────────────
  const timersRef = React.useRef<number[]>([]);
  const addTimer = (id: number) => timersRef.current.push(id);
  const clearTimers = React.useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  // ── Session state refs ────────────────────────────────────────────────────
  const isTrainingRef = React.useRef(false);
  const currentLevelRef = React.useRef<string | null>(null);
  const blocksRef = React.useRef<MathEffortBlockResult[]>([]);
  const levelChangesRef = React.useRef<MathEffortLevelChangeEvent[]>([]);
  const doneCountRef = React.useRef(0);
  const correctRef = React.useRef(0);
  const errorsRef = React.useRef(0);
  const omissionsRef = React.useRef(0);
  const earnedRef = React.useRef(0);
  const maxPossibleRef = React.useRef(0);
  const currentNumbersRef = React.useRef<number[]>([]);
  const correctAnswerRef = React.useRef(0);
  const responseStartRef = React.useRef(0);
  const awaitingRef = React.useRef(false);
  const answerInputRef = React.useRef<HTMLInputElement>(null);
  // Contagem de início: guarda a ação a executar ao fim e evita disparos duplos
  // (botão + barra de espaço) durante a contagem.
  const startCountdownActionRef = React.useRef<(() => void) | null>(null);
  const isCountingDownRef = React.useRef(false);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [phase, setPhase] = React.useState<Phase>("intro");
  const [introPageIndex, setIntroPageIndex] = React.useState(0);
  const [trainingDonePageIndex, setTrainingDonePageIndex] = React.useState(0);
  const [selectedLevel, setSelectedLevel] = React.useState<string | null>(firstLevelKey);
  const [currentLevel, setCurrentLevel] = React.useState<string | null>(null);
  const [currentProblemNumber, setCurrentProblemNumber] = React.useState(1);
  const [completedProblems, setCompletedProblems] = React.useState(0);
  const [displayedNumber, setDisplayedNumber] = React.useState<number | null>(null);
  const [answerInput, setAnswerInput] = React.useState("");
  const [remainingMs, setRemainingMs] = React.useState(cfg.task.response_time_limit_ms);
  const [motivationalMessage, setMotivationalMessage] = React.useState<string | null>(null);
  const [feedbackInfo, setFeedbackInfo] = React.useState<{
    outcome: TrainingFeedbackOutcome;
    correctAnswer: number;
    userAnswer: number | null;
  } | null>(null);
  const [finalAnswer, setFinalAnswer] = React.useState<MathEffortTaskAnswer | undefined>();
  const [pendingLevelKey, setPendingLevelKey] = React.useState<string | null>(null);
  const [countdownValue, setCountdownValue] = React.useState(0);
  const [isStartingCountdown, setIsStartingCountdown] = React.useState(false);
  const [trainingResults, setTrainingResults] = React.useState<{
    correct: number;
    errors: number;
    omissions: number;
    earned: number;
    maxPossible: number;
  } | null>(null);

  // ── Active config helpers ─────────────────────────────────────────────────
  const getActiveTimingConfig = React.useCallback((): Pick<
    ResolvedTrainingConfig,
    "numbers_per_problem" | "display_time_ms" | "response_time_limit_ms" | "mobile_response_time_limit_ms" | "inter_stimulus_interval_ms"
  > => {
    if (isTrainingRef.current && cfg.training) return cfg.training;
    return cfg.task;
  }, [cfg]);

  const getResponseLimit = React.useCallback(() => {
    const ac = getActiveTimingConfig();
    return isCoarsePointerDevice() ? ac.mobile_response_time_limit_ms : ac.response_time_limit_ms;
  }, [getActiveTimingConfig]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const currentBlock = () => blocksRef.current[blocksRef.current.length - 1] ?? null;

  const buildAnswer = (): MathEffortTaskAnswer => ({
    total_correct: correctRef.current,
    total_errors: errorsRef.current,
    total_omissions: omissionsRef.current,
    score: { earned: earnedRef.current, max_possible: maxPossibleRef.current },
    blocks: blocksRef.current.map((b) => ({
      ...b,
      problems: b.problems.map((p) => ({ ...p, numbers: [...p.numbers] })),
    })),
    level_change_events: levelChangesRef.current.map((e) => ({ ...e })),
  });

  // ── Core flow ─────────────────────────────────────────────────────────────
  const finishTask = React.useCallback(() => {
    clearTimers();
    awaitingRef.current = false;
    const answer = buildAnswer();
    setDisplayedNumber(null);
    setAnswerInput("");
    setRemainingMs(0);
    setMotivationalMessage(null);
    setFinalAnswer(answer);
    setPhase("done");
    onAnswerChangeRef.current(answer);
  }, [clearTimers]); // eslint-disable-line react-hooks/exhaustive-deps

  const finishTraining = React.useCallback(() => {
    clearTimers();
    awaitingRef.current = false;
    setTrainingResults({
      correct: correctRef.current,
      errors: errorsRef.current,
      omissions: omissionsRef.current,
      earned: earnedRef.current,
      maxPossible: maxPossibleRef.current,
    });
    setDisplayedNumber(null);
    setAnswerInput("");
    setRemainingMs(0);
    setMotivationalMessage(null);
    setTrainingDonePageIndex(0);
    setPhase("training_done");
  }, [clearTimers]);

  const startLevelBlock = React.useCallback(
    (levelKey: string) => {
      const fromKey = currentLevelRef.current;
      const fromLevel = fromKey === null ? null : levelNumber(fromKey, 0);
      const toLevel = levelNumber(levelKey, 0);
      const atProblem = doneCountRef.current + 1;

      if (!isTrainingRef.current && (fromLevel !== toLevel || fromLevel === null)) {
        levelChangesRef.current = [
          ...levelChangesRef.current,
          { at_problem: atProblem, from_level: fromLevel, to_level: toLevel },
        ];
      }

      currentLevelRef.current = levelKey;
      setCurrentLevel(levelKey);

      const pointValue = cfg.difficulty_levels[levelKey]?.point_value ?? 0;
      blocksRef.current = [
        ...blocksRef.current,
        {
          block_number: blocksRef.current.length + 1,
          level_chosen: toLevel,
          correct_count: 0,
          error_count: 0,
          omission_count: 0,
          point_value: pointValue,
          score: { earned: 0, max_possible: 0 },
          problems: [],
        },
      ];
    },
    [cfg.difficulty_levels],
  );

  // Forward declaration so startProblem ↔ continueAfterProblem can reference each other
  const startProblemRef = React.useRef<() => void>(() => { });

  const continueAfterProblem = React.useCallback(() => {
    if (isTrainingRef.current && cfg.training) {
      const totalTraining = levelEntries.length * cfg.training.problems_per_level;
      const done = doneCountRef.current;

      const msg = cfg.training.motivational_messages.find((m: { at_problem: number; message: string }) => m.at_problem === done)?.message ?? null;
      if (msg) {
        setMotivationalMessage(msg);
        setPhase("motivation");
        return;
      }

      if (done >= totalTraining) {
        finishTraining();
        return;
      }

      const newLevelIdx = Math.floor(done / cfg.training.problems_per_level);
      const curLevelIdx = levelEntries.findIndex(([k]: [string, ResolvedDifficultyLevel]) => k === currentLevelRef.current);
      if (newLevelIdx > curLevelIdx && newLevelIdx < levelEntries.length) {
        startLevelBlock(levelEntries[newLevelIdx][0]);
      }

      startProblemRef.current();
      return;
    }

    // Task mode
    if (doneCountRef.current >= cfg.task.total_problems) {
      finishTask();
      return;
    }
    const msg = cfg.task.motivational_messages.find((m: { at_problem: number; message: string }) => m.at_problem === doneCountRef.current)?.message ?? null;
    if (msg) {
      setMotivationalMessage(msg);
      setPhase("motivation");
      return;
    }
    const block = currentBlock();
    if (block && block.problems.length >= cfg.task.block_size) {
      setSelectedLevel(currentLevelRef.current ?? firstLevelKey);
      setPhase("select_level");
      return;
    }
    startProblemRef.current();
  }, [cfg.training, cfg.task, levelEntries, finishTraining, finishTask, firstLevelKey, startLevelBlock]); // eslint-disable-line react-hooks/exhaustive-deps

  // Exibe feedback de certo/errado apenas no treino; fora do treino segue direto.
  const proceedAfterProblem = React.useCallback(
    (outcome: TrainingFeedbackOutcome, correctAnswer: number, userAnswer: number | null) => {
      if (isTrainingRef.current) {
        setFeedbackInfo({ outcome, correctAnswer, userAnswer });
        setPhase("feedback");
        const durationMs =
          outcome === "correct" ? TRAINING_FEEDBACK_CORRECT_MS : TRAINING_FEEDBACK_ERROR_MS;
        addTimer(
          window.setTimeout(() => {
            setFeedbackInfo(null);
            continueAfterProblem();
          }, durationMs),
        );
        return;
      }
      continueAfterProblem();
    },
    [continueAfterProblem],
  );

  const startProblem = React.useCallback(() => {
    clearTimers();

    if (!isTrainingRef.current && doneCountRef.current >= cfg.task.total_problems) {
      finishTask();
      return;
    }

    const levelKey = currentLevelRef.current;
    const range = levelKey ? cfg.difficulty_levels[levelKey] : null;
    if (!levelKey || !range) {
      setPhase("select_level");
      return;
    }

    const ac = getActiveTimingConfig();
    const responseLimit = getResponseLimit();

    const nums = Array.from({ length: ac.numbers_per_problem }, () => randInt(range.min, range.max));
    currentNumbersRef.current = nums;
    correctAnswerRef.current = nums.reduce((a, b) => a + b, 0);
    awaitingRef.current = false;

    const problemNumber = doneCountRef.current + 1;
    setCurrentProblemNumber(problemNumber);
    setAnswerInput("");
    setRemainingMs(responseLimit);
    setDisplayedNumber(nums[0]);
    setPhase("showing_numbers");

    const showAt = (idx: number) => {
      setDisplayedNumber(nums[idx]);
      addTimer(
        window.setTimeout(() => {
          const isLast = idx >= nums.length - 1;
          if (isLast) {
            setDisplayedNumber(null);
            setPhase("answering");
            responseStartRef.current = performance.now();
            awaitingRef.current = true;
            setRemainingMs(responseLimit);

            addTimer(
              window.setInterval(() => {
                setRemainingMs(
                  Math.max(0, responseLimit - Math.round(performance.now() - responseStartRef.current)),
                );
              }, 100) as unknown as number,
            );

            addTimer(
              window.setTimeout(() => {
                if (!awaitingRef.current) return;
                awaitingRef.current = false;
                clearTimers();
                const block = currentBlock();
                if (!block) return;
                const pointValue = block.point_value;
                block.problems = [
                  ...block.problems,
                  {
                    problem_number: problemNumber,
                    numbers: [...currentNumbersRef.current],
                    correct_answer: correctAnswerRef.current,
                    user_answer: null,
                    is_correct: false,
                    response_time_ms: responseLimit,
                  },
                ];
                block.omission_count += 1;
                block.score.max_possible += pointValue;
                omissionsRef.current += 1;
                maxPossibleRef.current += pointValue;
                doneCountRef.current += 1;
                setCompletedProblems(doneCountRef.current);
                proceedAfterProblem("omission", correctAnswerRef.current, null);
              }, responseLimit),
            );

            addTimer(window.setTimeout(() => answerInputRef.current?.focus(), 50));
            return;
          }

          setDisplayedNumber(null);
          addTimer(window.setTimeout(() => showAt(idx + 1), ac.inter_stimulus_interval_ms));
        }, ac.display_time_ms),
      );
    };

    showAt(0);
  }, [clearTimers, cfg, getActiveTimingConfig, getResponseLimit, proceedAfterProblem, finishTask]); // eslint-disable-line react-hooks/exhaustive-deps

  startProblemRef.current = startProblem;

  const submitAnswer = React.useCallback(() => {
    if (phase !== "answering") return;
    const trimmed = answerInput.trim();
    if (!/^\d+$/.test(trimmed) || !awaitingRef.current) return;
    awaitingRef.current = false;

    const responseLimit = getResponseLimit();
    const userAnswer = Number.parseInt(trimmed, 10);
    const responseTime = Math.min(responseLimit, Math.max(0, Math.round(performance.now() - responseStartRef.current)));
    clearTimers();

    const isCorrect = userAnswer === correctAnswerRef.current;
    const block = currentBlock();
    if (!block) return;
    const pointValue = block.point_value;

    block.problems = [
      ...block.problems,
      {
        problem_number: currentProblemNumber,
        numbers: [...currentNumbersRef.current],
        correct_answer: correctAnswerRef.current,
        user_answer: userAnswer,
        is_correct: isCorrect,
        response_time_ms: responseTime,
      },
    ];
    block.score.max_possible += pointValue;
    maxPossibleRef.current += pointValue;
    if (isCorrect) {
      block.correct_count += 1;
      block.score.earned += pointValue;
      correctRef.current += 1;
      earnedRef.current += pointValue;
    } else {
      block.error_count += 1;
      errorsRef.current += 1;
    }

    doneCountRef.current += 1;
    setCompletedProblems(doneCountRef.current);
    proceedAfterProblem(isCorrect ? "correct" : "error", correctAnswerRef.current, userAnswer);
  }, [answerInput, getResponseLimit, clearTimers, proceedAfterProblem, currentProblemNumber, phase]);

  const continueAfterMotivation = React.useCallback(() => {
    setMotivationalMessage(null);

    if (isTrainingRef.current && cfg.training) {
      const totalTraining = levelEntries.length * cfg.training.problems_per_level;
      const done = doneCountRef.current;
      if (done >= totalTraining) {
        finishTraining();
        return;
      }
      const newLevelIdx = Math.floor(done / cfg.training.problems_per_level);
      const curLevelIdx = levelEntries.findIndex(([k]: [string, ResolvedDifficultyLevel]) => k === currentLevelRef.current);
      if (newLevelIdx > curLevelIdx && newLevelIdx < levelEntries.length) {
        startLevelBlock(levelEntries[newLevelIdx][0]);
      }
      startProblem();
      return;
    }

    const block = currentBlock();
    if (block && block.problems.length >= cfg.task.block_size) {
      setSelectedLevel(currentLevelRef.current ?? firstLevelKey);
      setPhase("select_level");
      return;
    }
    startProblem();
  }, [cfg.training, cfg.task.block_size, levelEntries, finishTraining, firstLevelKey, startLevelBlock, startProblem]);

  const resetSession = React.useCallback(() => {
    currentLevelRef.current = null;
    blocksRef.current = [];
    levelChangesRef.current = [];
    doneCountRef.current = 0;
    correctRef.current = 0;
    errorsRef.current = 0;
    omissionsRef.current = 0;
    earnedRef.current = 0;
    maxPossibleRef.current = 0;
    currentNumbersRef.current = [];
    correctAnswerRef.current = 0;
    responseStartRef.current = 0;
    awaitingRef.current = false;
  }, []);

  const resetTask = React.useCallback(() => {
    clearTimers();
    isTrainingRef.current = false;
    isCountingDownRef.current = false;
    startCountdownActionRef.current = null;
    resetSession();
    setIsStartingCountdown(false);
    setCountdownValue(0);
    setPhase("intro");
    setIntroPageIndex(0);
    setTrainingDonePageIndex(0);
    setSelectedLevel(firstLevelKey);
    setCurrentLevel(null);
    setCurrentProblemNumber(1);
    setCompletedProblems(0);
    setDisplayedNumber(null);
    setAnswerInput("");
    setRemainingMs(cfg.task.response_time_limit_ms);
    setMotivationalMessage(null);
    setFeedbackInfo(null);
    setFinalAnswer(undefined);
    setTrainingResults(null);
    onAnswerChangeRef.current(undefined);
  }, [clearTimers, resetSession, firstLevelKey, cfg.task.response_time_limit_ms]);

  React.useEffect(() => {
    resetTask();
    return clearTimers;
  }, [resetTask, clearTimers]);

  const startTraining = React.useCallback(() => {
    clearTimers();
    isTrainingRef.current = true;
    resetSession();
    setCurrentLevel(null);
    setSelectedLevel(firstLevelKey);
    setCurrentProblemNumber(1);
    setCompletedProblems(0);
    setDisplayedNumber(null);
    setAnswerInput("");
    setMotivationalMessage(null);
    if (levelEntries.length > 0) {
      const firstKey = levelEntries[0][0];
      // startLevelBlock sets currentLevelRef and creates first block
      const pointValue = cfg.difficulty_levels[firstKey]?.point_value ?? 0;
      currentLevelRef.current = firstKey;
      setCurrentLevel(firstKey);
      blocksRef.current = [
        {
          block_number: 1,
          level_chosen: levelNumber(firstKey, 0),
          correct_count: 0,
          error_count: 0,
          omission_count: 0,
          point_value: pointValue,
          score: { earned: 0, max_possible: 0 },
          problems: [],
        },
      ];
      startProblemRef.current();
    }
  }, [clearTimers, resetSession, firstLevelKey, levelEntries, cfg.difficulty_levels]);

  const startTaskAfterTraining = React.useCallback(() => {
    clearTimers();
    isTrainingRef.current = false;
    resetSession();
    setCurrentLevel(null);
    setSelectedLevel(firstLevelKey);
    setCurrentProblemNumber(1);
    setCompletedProblems(0);
    setDisplayedNumber(null);
    setAnswerInput("");
    setRemainingMs(cfg.task.response_time_limit_ms);
    setMotivationalMessage(null);
    setPhase("select_level");
  }, [clearTimers, resetSession, firstLevelKey, cfg.task.response_time_limit_ms]);

  // ── Contagem regressiva de início ──────────────────────────────────────────
  // Exibe "Começando em N..." e só então executa a ação (iniciar treino,
  // iniciar tarefa ou iniciar o bloco do nível confirmado).
  const runWithStartCountdown = React.useCallback(
    (action: () => void) => {
      if (isCountingDownRef.current) return;
      clearTimers();
      startCountdownActionRef.current = action;
      isCountingDownRef.current = true;
      setIsStartingCountdown(true);
      setCountdownValue(START_COUNTDOWN_SECONDS);

      const tick = (remaining: number) => {
        if (remaining <= 0) {
          isCountingDownRef.current = false;
          setIsStartingCountdown(false);
          setCountdownValue(0);
          const pending = startCountdownActionRef.current;
          startCountdownActionRef.current = null;
          pending?.();
          return;
        }
        setCountdownValue(remaining);
        addTimer(window.setTimeout(() => tick(remaining - 1), 1000));
      };

      addTimer(window.setTimeout(() => tick(START_COUNTDOWN_SECONDS - 1), 1000));
    },
    [clearTimers],
  );

  // ── Intro pagination ───────────────────────────────────────────────────────
  const startTaskFlow = React.useCallback(() => {
    if (!hasDifficultyLevels) return;
    clearTimers();
    if (hasTraining) {
      startTraining();
    } else {
      setPhase("select_level");
      setSelectedLevel(firstLevelKey);
    }
  }, [hasDifficultyLevels, clearTimers, hasTraining, startTraining, firstLevelKey]);

  const advanceMathIntro = React.useCallback(() => {
    if (isCountingDownRef.current) return;
    if (introPageIndex < introductionPages.length - 1) {
      setIntroPageIndex((prev) => prev + 1);
      return;
    }
    if (!hasDifficultyLevels) return;
    runWithStartCountdown(startTaskFlow);
  }, [introPageIndex, introductionPages.length, hasDifficultyLevels, runWithStartCountdown, startTaskFlow]);

  // Fim do treino: página 0 = resultados; páginas 1..N = trainingFinishPages.
  const advanceTrainingDone = React.useCallback(() => {
    if (isCountingDownRef.current) return;
    if (trainingDonePageIndex < trainingFinishPages.length) {
      setTrainingDonePageIndex((prev) => prev + 1);
      return;
    }
    runWithStartCountdown(startTaskAfterTraining);
  }, [trainingDonePageIndex, trainingFinishPages.length, runWithStartCountdown, startTaskAfterTraining]);

  // ── Keyboard: avança as páginas de introdução / fim de treino com a barra de espaço ─
  React.useEffect(() => {
    if (phase !== "intro" && phase !== "training_done") return;
    const onKeyDown = (e: KeyboardEvent) => {
      const isSpace = e.code === "Space" || e.key === " ";
      if (!isSpace || e.repeat) return;
      e.preventDefault();
      if (phase === "intro") {
        advanceMathIntro();
      } else {
        advanceTrainingDone();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [phase, advanceMathIntro, advanceTrainingDone]);

  // Confirmação na tela de seleção: conta e só então inicia o bloco do nível.
  const confirmSelectedLevel = React.useCallback(() => {
    if (!selectedLevel || !hasDifficultyLevels) return;
    const levelKey = selectedLevel;
    runWithStartCountdown(() => {
      startLevelBlock(levelKey);
      startProblem();
    });
  }, [selectedLevel, hasDifficultyLevels, runWithStartCountdown, startLevelBlock, startProblem]);

  // ── Level change (mid-task) ───────────────────────────────────────────────
  const commitLevelChange = React.useCallback((nextKey: string) => {
    startLevelBlock(nextKey);
    startProblem();
  }, [startLevelBlock, startProblem]);

  const handleLevelChange = (nextKey: string) => {
    if (phase === "select_level") {
      setSelectedLevel(nextKey);
      return;
    }
    if (nextKey === currentLevelRef.current) return;
    if (phase === "level_changing") {
      // update pending target mid-countdown
      setPendingLevelKey(nextKey);
      return;
    }

    if (phase === "showing_numbers" || phase === "answering") {
      clearTimers();
      awaitingRef.current = false;
      // Discard current problem entirely — not saved, not counted
      currentNumbersRef.current = [];
      setDisplayedNumber(null);
      setAnswerInput("");
    } else if (phase === "motivation") {
      setMotivationalMessage(null);
    }

    const changeMs = cfg.task.difficulty_change_time_ms;
    if (changeMs <= 0) {
      commitLevelChange(nextKey);
      return;
    }

    // Start countdown
    const totalSteps = Math.ceil(changeMs / 1000);
    setPendingLevelKey(nextKey);
    setCountdownValue(totalSteps);
    setPhase("level_changing");

    let remaining = totalSteps;
    const tick = () => {
      remaining -= 1;
      if (remaining <= 0) {
        // read the latest pending key via ref
        setPendingLevelKey((latest: string | null) => {
          commitLevelChange(latest ?? nextKey);
          return null;
        });
        setCountdownValue(0);
        return;
      }
      setCountdownValue(remaining);
      addTimer(window.setTimeout(tick, 1000));
    };
    addTimer(window.setTimeout(tick, 1000));
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const isActive = phase === "showing_numbers" || phase === "answering" || phase === "feedback" || phase === "motivation" || phase === "level_changing";

  const safeIntroPageIndex = Math.min(introPageIndex, Math.max(0, introductionPages.length - 1));
  const currentIntroPage = introductionPages[safeIntroPageIndex];
  const isLastIntroPage = safeIntroPageIndex >= introductionPages.length - 1;

  // Fim do treino: índice 0 = resultados; índices 1..N mapeiam trainingFinishPages[idx - 1].
  const safeTrainingDoneIndex = Math.min(trainingDonePageIndex, trainingFinishPages.length);
  const isTrainingResultsPage = safeTrainingDoneIndex === 0;
  const currentFinishPage = isTrainingResultsPage
    ? null
    : trainingFinishPages[safeTrainingDoneIndex - 1];
  const isLastTrainingDonePage = safeTrainingDoneIndex >= trainingFinishPages.length;

  // ── Progresso do treino / da tarefa ────────────────────────────────────────
  // O total vem das mesmas expressões que decidem o fim de cada etapa:
  // níveis × problems_per_level no treino, total_problems na tarefa. Assim o
  // contador acompanha automaticamente a configuração em vigor.
  const isTrainingPhase = isTrainingRef.current;
  const totalProblemsInPhase =
    isTrainingPhase && cfg.training
      ? levelEntries.length * cfg.training.problems_per_level
      : cfg.task.total_problems;
  // Na tela de seleção de nível o problema atual já foi respondido, então o
  // contador aponta para o próximo; nas demais fases, para o que está em curso.
  const displayedProblemNumber =
    totalProblemsInPhase <= 0
      ? 0
      : Math.min(
          totalProblemsInPhase,
          phase === "select_level" ? completedProblems + 1 : Math.max(currentProblemNumber, 1),
        );
  const progressPercentage =
    totalProblemsInPhase > 0 ? (displayedProblemNumber / totalProblemsInPhase) * 100 : 0;
  const showProgress =
    (isActive || phase === "select_level") && totalProblemsInPhase > 0;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 760,
        boxSizing: "border-box",
        margin: "0 auto",
        mt: 4,
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        backgroundColor: "grey.100",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {/* PROGRESSO (treino e tarefa) — topo do card, fora da área do estímulo
          central, para não competir com os números que aparecem no centro. */}
      {showProgress && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            columnGap: 1.5,
            rowGap: 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Typography variant="body1" color="primary" fontWeight={700} noWrap>
              {isTrainingPhase ? "Treino" : "Tarefa"}
            </Typography>
            <Box
              sx={{
                width: { xs: 80, sm: 120 },
                height: 5,
                flexShrink: 0,
                backgroundColor: "grey.300",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${progressPercentage}%`,
                  backgroundColor: "primary.main",
                  transition: "width 0.2s linear",
                }}
              />
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" noWrap>
            {`Problema ${displayedProblemNumber} de ${totalProblemsInPhase}`}
          </Typography>
        </Box>
      )}

      {/* INTRO */}
      {phase === "intro" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="h4" color="primary" sx={{ textAlign: "center", fontWeight: "700" }}>
              {richText(currentIntroPage.title)}
            </Typography>
          </Box>

          {currentIntroPage.instructions.length > 0 && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box component="ul" sx={{ listStyleType: "disc", paddingLeft: "1.25rem", margin: 0 }}>
                {currentIntroPage.instructions.map((line: string, i: number) => (
                  <Box component="li" key={i} sx={{ marginBottom: "0.3rem" }}>
                    <Typography variant="body1" component="div" color="text.secondary">{richText(line)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Níveis de dificuldade — exibidos apenas nas páginas marcadas */}
          {currentIntroPage.show_difficulty_levels && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Níveis de dificuldade
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "repeat(auto-fill, minmax(100px, 1fr))", sm: "repeat(5, 1fr)" },
                  gap: 1,
                }}
              >
                {levelEntries.map(([key, range]: [string, ResolvedDifficultyLevel], idx: number) => {
                  const label = levelNumber(key, 0);
                  const color = difficultyColor(idx, levelEntries.length);
                  return (
                    <Box
                      key={key}
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: color,
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="body1" fontWeight={700} sx={{ color }}>
                        Nível {label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" display="block">
                        {range.min} – {range.max}
                      </Typography>
                      <Typography variant="body2" color="text.disabled">
                        {range.point_value} pt
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {(currentIntroPage.show_difficulty_levels || isLastIntroPage) && !hasDifficultyLevels && (
            <Alert severity="error">Nenhum nível foi configurado em `difficulty_levels`.</Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            {isStartingCountdown ? (
              <Typography variant="h5" color="primary">
                {`Começando em ${countdownValue}...`}
              </Typography>
            ) : (
              <>
                {isLastIntroPage ? (
                  <Button
                    variant="contained"
                    size="medium"
                    startIcon={<PlayArrowRoundedIcon />}
                    onClick={advanceMathIntro}
                    disabled={!hasDifficultyLevels}
                  >
                    {hasTraining ? "Iniciar treino" : "Iniciar tarefa"}
                  </Button>
                ) : (
                  <Button variant="contained" size="medium" onClick={advanceMathIntro}>
                    Continuar
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary">
                  Pressione o botão ou a barra de espaço para dar seguimento.
                </Typography>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* TRAINING DONE */}
      {phase === "training_done" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "center", alignItems: "center" }}>
          {isTrainingResultsPage ? (
            <>
              <Typography variant="h4" color="primary">
                Treino concluído!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Resultados do treino
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}>
                <Typography variant="h6">Corretas: {trainingResults?.correct ?? 0}</Typography>
                <Typography variant="h6">Erros: {trainingResults?.errors ?? 0}</Typography>
                <Typography variant="h6">Omissões: {trainingResults?.omissions ?? 0}</Typography>
                <Typography variant="h6">
                  Pontuação: {trainingResults?.earned ?? 0} / {trainingResults?.maxPossible ?? 0}
                </Typography>
              </Box>
            </>
          ) : (
            <>
              {currentFinishPage?.title && (
                <Typography variant="h4" color="primary">
                  {richText(currentFinishPage.title)}
                </Typography>
              )}
              <Box
                component="ul"
                sx={{
                  textAlign: "left",
                  listStyleType: "disc",
                  paddingLeft: "1.25rem",
                  margin: 0,
                  width: "100%",
                }}
              >
                {(currentFinishPage?.instructions ?? []).map((line: string, i: number) => (
                  <Box component="li" key={i} sx={{ marginBottom: "0.4rem" }}>
                    <Typography variant="body1" component="div" color="text.secondary">
                      {richText(line)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            {isStartingCountdown ? (
              <Typography variant="h5" color="primary">
                {`Começando em ${countdownValue}...`}
              </Typography>
            ) : (
              <>
                {isLastTrainingDonePage ? (
                  <Button
                    variant="contained"
                    startIcon={<PlayArrowRoundedIcon />}
                    onClick={advanceTrainingDone}
                  >
                    Iniciar tarefa
                  </Button>
                ) : (
                  <Button variant="contained" onClick={advanceTrainingDone}>
                    Continuar
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary">
                  Pressione o botão ou a barra de espaço para dar seguimento.
                </Typography>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* LEVEL SELECT */}
      {phase === "select_level" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h5" color="primary" sx={{ textAlign: "center" }}>
            Escolha o nível para os próximos {cfg.task.block_size} problemas
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr" }, gap: 1 }}>
            {levelEntries.map(([key, range]: [string, ResolvedDifficultyLevel], idx: number) => {
              const label = levelNumber(key, 0);
              const color = difficultyColor(idx, levelEntries.length);
              const isSelected = selectedLevel === key;
              return (
                <Box
                  key={key}
                  onClick={() => {
                    if (isStartingCountdown) return;
                    setSelectedLevel(key);
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 0.5,
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: isSelected ? color : "divider",
                    backgroundColor: isSelected ? `${color}10` : "background.paper",
                    cursor: isStartingCountdown ? "default" : "pointer",
                    opacity: isStartingCountdown && !isSelected ? 0.6 : 1,
                    transition: "all 0.15s",
                    "&:hover": isStartingCountdown
                      ? undefined
                      : { borderColor: color, backgroundColor: `${color}18` },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="body1" fontWeight={700} sx={{ color }}>
                      Nível {label}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Números de {range.min} a {range.max}
                    </Typography>
                    <Typography variant="body1" color="text.disabled">
                      {range.point_value} pt/acerto
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.disabled" sx={{ fontFamily: "monospace" }}>
                    ex: {range.min} + {range.max} + {range.min + 1} + {range.max - 1}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          {!hasDifficultyLevels && <Alert severity="error">Nenhum nível disponível para continuar.</Alert>}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            {isStartingCountdown ? (
              <Typography variant="h5" color="primary">
                {`Começando em ${countdownValue}...`}
              </Typography>
            ) : (
              <Button
                variant="contained"
                onClick={confirmSelectedLevel}
                disabled={!selectedLevel || !hasDifficultyLevels}
                startIcon={<CheckCircleRoundedIcon />}
              >
                Confirmar nível
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* LEVEL SWITCHER (during task) */}
      {isActive && currentLevel !== null && !isTrainingRef.current && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center" }}>
            {phase === "level_changing"
              ? "Trocar para outro nível (cancela a contagem atual):"
              : "Trocar nível (invalida a rodada atual e reinicia imediatamente):"}
          </Typography>
          <ToggleButtonGroup
            value={phase === "level_changing" ? pendingLevelKey : currentLevel}
            exclusive
            onChange={(_e: React.SyntheticEvent, key: string | null) => {
              if (key) handleLevelChange(key);
            }}
            sx={{ display: "flex", flexWrap: "wrap", backgroundColor: "background.paper", width: "100%" }}
          >
            {levelEntries.map(([key]: [string, unknown], idx: number) => {
              const label = levelNumber(key, 0);
              const color = difficultyColor(idx, levelEntries.length);
              return (
                <ToggleButton
                  key={key}
                  value={key}
                  sx={{
                    flex: "1 1 auto",
                    py: 0.75,
                    px: { xs: 2 },
                    textTransform: "none",
                    color,
                    fontWeight: 700,
                    borderColor: `#00000040`,
                    "&.Mui-selected": {
                      color,
                      backgroundColor: `${color}18`,
                      borderColor: color,
                      "&:hover": { backgroundColor: `${color}28` },
                    },
                    "&:hover": { borderColor: color, backgroundColor: `${color}10` },
                  }}
                >
                  Nível {label}
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
        </Box>
      )}

      {/* LEVEL CHANGING countdown */}
      {phase === "level_changing" && pendingLevelKey !== null && (
        <Box
          sx={{
            minHeight: 220,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            textAlign: "center",
          }}
        >
          {(() => {
            const idx = levelEntries.findIndex(([k]: [string, ResolvedDifficultyLevel]) => k === pendingLevelKey);
            const color = difficultyColor(idx, levelEntries.length);
            return (
              <>
                <Typography variant="h6" color="text.secondary">
                  Iniciando nível em {countdownValue}..
                </Typography>
                <Typography variant="h3" fontWeight={700} sx={{ color }}>
                  Nível {levelNumber(pendingLevelKey, 0)}
                </Typography>
              </>
            );
          })()}
        </Box>
      )}

      {/* Training level indicator */}
      {isActive && isTrainingRef.current && currentLevel !== null && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {(() => {
            const idx = levelEntries.findIndex(([k]: [string, ResolvedDifficultyLevel]) => k === currentLevel);
            const color = difficultyColor(idx, levelEntries.length);
            return (
              <Typography variant="body1" sx={{ color, fontWeight: 700 }}>
                {/* O rótulo "Treino" já aparece no cabeçalho de progresso. */}
                Nível {levelNumber(currentLevel, 0)}
              </Typography>
            );
          })()}
        </Box>
      )}

      {/* SHOWING NUMBERS */}
      {phase === "showing_numbers" && (
        <Box
          sx={{
            minHeight: 220,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="h1"
            color="primary"
            sx={{ fontSize: { xs: "4.2rem", md: "6rem" }, minHeight: "6.5rem" }}
          >
            {displayedNumber ?? ""}
          </Typography>
        </Box>
      )}

      {/* ANSWERING */}
      {phase === "answering" && (
        <Box
          sx={{
            minHeight: 220,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Typography variant="h5" color="primary">
            Digite a soma
          </Typography>

          {(() => {
            const responseLimit = getResponseLimit();
            const pct = remainingMs / responseLimit;
            const barColor = pct > 0.5 ? "#2E7D32" : pct > 0.25 ? "#E65100" : "#B71C1C";
            return (
              <Box
                sx={{
                  width: { xs: "100%", sm: 240 },
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: "grey.200",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${pct * 100}%`,
                    backgroundColor: barColor,
                    transition: "width 0.1s linear, background-color 0.3s",
                  }}
                />
              </Box>
            );
          })()}

          <TextField
            inputRef={answerInputRef}
            value={answerInput}
            type="text"
            autoComplete="off"
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*", autoComplete: "off" }}
            onChange={(e) => {
              if (/^\d*$/.test(e.target.value)) setAnswerInput(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitAnswer();
              }
            }}
            placeholder="Resposta"
            sx={{ width: { xs: "100%", sm: 240 } }}
          />
          <Typography variant="body2" color="text.secondary">
            Pressione Enter para confirmar
          </Typography>
          <Button variant="contained" disabled={!/^\d+$/.test(answerInput.trim())} onClick={submitAnswer}>
            Confirmar
          </Button>
        </Box>
      )}

      {/* FEEDBACK (somente treino) */}
      {phase === "feedback" && feedbackInfo && (
        <Box
          sx={{
            minHeight: 220,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            textAlign: "center",
          }}
        >
          {feedbackInfo.outcome === "correct" ? (
            <>
              <CheckCircleOutlineIcon sx={{ color: "#43a047", fontSize: { xs: "3.5rem", md: "5rem" } }} />
              <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                Correto!
              </Typography>
            </>
          ) : (
            <>
              <HighlightOffIcon sx={{ color: "#ff1744", fontSize: { xs: "3.5rem", md: "5rem" } }} />
              <Typography variant="h4" sx={{ color: "#ff1744", fontWeight: 700 }}>
                {feedbackInfo.outcome === "omission" ? "Tempo esgotado!" : "Errado!"}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                A resposta correta era {feedbackInfo.correctAnswer}.
              </Typography>
            </>
          )}
        </Box>
      )}

      {/* MOTIVATION */}
      {phase === "motivation" && motivationalMessage && (
        <Box
          sx={{
            minHeight: 220,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="h4" color="primary">
            {richText(motivationalMessage)}
          </Typography>
          <Button variant="contained" onClick={continueAfterMotivation}>
            Continuar
          </Button>
        </Box>
      )}

      {/* DONE */}
      {phase === "done" && (
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 1, textAlign: "center", alignItems: "center" }}
        >
          <Typography variant="h4" color="primary">
            Tarefa concluída
          </Typography>
          <Typography variant="h6">Corretas: {finalAnswer?.total_correct ?? 0}</Typography>
          <Typography variant="h6">Erros: {finalAnswer?.total_errors ?? 0}</Typography>
          <Typography variant="h6">Omissões: {finalAnswer?.total_omissions ?? 0}</Typography>
          <Typography variant="h6">
            Pontuação: {finalAnswer?.score.earned ?? 0} / {finalAnswer?.score.max_possible ?? 0}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Clique em "Próximo" para continuar.
          </Typography>
        </Box>
      )}

    </Box>
  );
};

export default MathEffortQuestion;
